/**
 * Game Loop - Fixed Timestep Physics, Variable Render
 *
 * Core game loop optimized for 60fps with:
 * - Fixed timestep physics (deterministic)
 * - Variable render rate (smooth visuals)
 * - Frame time accumulator
 * - Performance monitoring
 * - Automatic throttling when tab inactive
 */

export interface GameLoopCallbacks {
  /** Fixed update for physics - called at fixed intervals (default 60Hz) */
  fixedUpdate: (fixedDeltaTime: number) => void;
  /** Variable update for game logic - called every frame */
  update: (deltaTime: number) => void;
  /** Render - called every frame after update */
  render: (interpolation: number) => void;
}

export interface GameLoopConfig {
  /** Target physics updates per second (default: 60) */
  fixedFps?: number;
  /** Maximum frame time to prevent spiral of death (default: 250ms) */
  maxFrameTime?: number;
  /** Enable performance monitoring (default: true in dev) */
  enablePerfMonitor?: boolean;
}

export interface PerformanceStats {
  fps: number;
  frameTime: number;
  physicsTime: number;
  renderTime: number;
  updateTime: number;
  physicsUpdatesPerFrame: number;
}

export class GameLoop {
  private callbacks: GameLoopCallbacks;
  private isRunning = false;
  private animationFrameId: number | null = null;

  // Timing
  private fixedDeltaTime: number;
  private maxFrameTime: number;
  private lastTime = 0;
  private accumulator = 0;

  // Performance monitoring
  private enablePerfMonitor: boolean;
  private frameCount = 0;
  private fpsTime = 0;
  private stats: PerformanceStats = {
    fps: 0,
    frameTime: 0,
    physicsTime: 0,
    renderTime: 0,
    updateTime: 0,
    physicsUpdatesPerFrame: 0,
  };

  // Visibility handling
  private isVisible = true;
  private boundHandleVisibility: () => void;
  private boundTick: (time: number) => void;

  constructor(callbacks: GameLoopCallbacks, config: GameLoopConfig = {}) {
    this.callbacks = callbacks;

    const fixedFps = config.fixedFps ?? 60;
    this.fixedDeltaTime = 1 / fixedFps;
    this.maxFrameTime = (config.maxFrameTime ?? 250) / 1000;
    this.enablePerfMonitor = config.enablePerfMonitor ?? (import.meta.env?.DEV ?? false);

    // Bind methods for event handlers
    this.boundHandleVisibility = this.handleVisibility.bind(this);
    this.boundTick = this.tick.bind(this);
  }

  /**
   * Start the game loop
   */
  start(): void {
    if (this.isRunning) return;

    this.isRunning = true;
    this.lastTime = performance.now() / 1000;
    this.accumulator = 0;
    this.frameCount = 0;
    this.fpsTime = this.lastTime;

    // Listen for visibility changes
    document.addEventListener('visibilitychange', this.boundHandleVisibility);

    // Start loop
    this.animationFrameId = requestAnimationFrame(this.boundTick);
  }

  /**
   * Stop the game loop
   */
  stop(): void {
    if (!this.isRunning) return;

    this.isRunning = false;

    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    document.removeEventListener('visibilitychange', this.boundHandleVisibility);
  }

  /**
   * Main tick function - called every animation frame
   */
  private tick(timeMs: number): void {
    if (!this.isRunning) return;

    const time = timeMs / 1000;
    let frameTime = time - this.lastTime;
    this.lastTime = time;

    // Clamp frame time to prevent spiral of death
    if (frameTime > this.maxFrameTime) {
      frameTime = this.maxFrameTime;
    }

    // Skip update if tab is hidden
    if (!this.isVisible) {
      this.animationFrameId = requestAnimationFrame(this.boundTick);
      return;
    }

    const frameStart = performance.now();
    let physicsTime = 0;
    let updateTime = 0;
    let physicsUpdates = 0;

    // Accumulate time for fixed update
    this.accumulator += frameTime;

    // Fixed timestep physics updates
    const physicsStart = performance.now();
    while (this.accumulator >= this.fixedDeltaTime) {
      this.callbacks.fixedUpdate(this.fixedDeltaTime);
      this.accumulator -= this.fixedDeltaTime;
      physicsUpdates++;

      // Safety: prevent infinite loop
      if (physicsUpdates > 10) {
        this.accumulator = 0;
        break;
      }
    }
    physicsTime = performance.now() - physicsStart;

    // Variable update (game logic)
    const updateStart = performance.now();
    this.callbacks.update(frameTime);
    updateTime = performance.now() - updateStart;

    // Render with interpolation
    const interpolation = this.accumulator / this.fixedDeltaTime;
    const renderStart = performance.now();
    this.callbacks.render(interpolation);
    const renderTime = performance.now() - renderStart;

    // Performance monitoring
    if (this.enablePerfMonitor) {
      this.frameCount++;
      if (time - this.fpsTime >= 1.0) {
        this.stats.fps = this.frameCount / (time - this.fpsTime);
        this.stats.frameTime = performance.now() - frameStart;
        this.stats.physicsTime = physicsTime;
        this.stats.updateTime = updateTime;
        this.stats.renderTime = renderTime;
        this.stats.physicsUpdatesPerFrame = physicsUpdates;

        this.frameCount = 0;
        this.fpsTime = time;
      }
    }

    // Schedule next frame
    this.animationFrameId = requestAnimationFrame(this.boundTick);
  }

  /**
   * Handle visibility change (tab switch)
   */
  private handleVisibility(): void {
    this.isVisible = !document.hidden;

    if (this.isVisible) {
      // Reset timing when returning to tab
      this.lastTime = performance.now() / 1000;
      this.accumulator = 0;
    }
  }

  /**
   * Get current performance stats
   */
  getStats(): Readonly<PerformanceStats> {
    return this.stats;
  }

  /**
   * Check if loop is running
   */
  get running(): boolean {
    return this.isRunning;
  }

  /**
   * Get the fixed delta time (for physics)
   */
  get fixedDelta(): number {
    return this.fixedDeltaTime;
  }
}
